import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC } from 'react';

const useStyles = makeStyles((theme: any) => ({
  // Name and actions share a row from sm; on phones Delete drops below the name.
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(3, 2) },
  },
  identity: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1, 2),
    minWidth: 0,
    // Phones: name and badge pinned to opposite edges of the paper.
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
  },
  title: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  chip: {
    flexShrink: 0,
    color: theme.palette.common.white,
    // Centre the badge on the first line of a wrapped name (h6 line ≈ 32px, chip 24px).
    [theme.breakpoints.down('sm')]: { marginTop: theme.spacing(0.5) },
  },
  // Destructive action: error palette, never the amber CTA (DESIGN.md §5).
  deleteButton: {
    textTransform: 'none',
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.error.main,
    },
    [theme.breakpoints.down('sm')]: { width: '100%' },
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  icon: {
    fontSize: 16,
    marginRight: theme.spacing(1),
  },
}));

type HeaderProps = {
  t: TFunction;
  name: string;
  username: string;
  isTitleCompany: boolean;
  handleOpenConfirmModal: (state: boolean, kind: 'delete' | 'unlink') => void;
};

const Header: FC<HeaderProps> = ({
  t,
  name,
  username,
  isTitleCompany,
  handleOpenConfirmModal,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.identity}>
        <Typography variant="h6" component="h1" className={classes.title}>
          {name}
        </Typography>

        {username?.trim() && (
          <Chip
            size="small"
            color="secondary"
            label={t('users:user-system')}
            className={classes.chip}
          />
        )}
      </div>

      {!isTitleCompany && (
        <Button
          variant="outlined"
          className={classes.deleteButton}
          onClick={() => handleOpenConfirmModal(true, 'delete')}
        >
          <FontAwesomeIcon className={classes.icon} icon="trash-alt" />
          {t('users:delete')}
        </Button>
      )}
    </div>
  );
};

export default Header;
